// src/hooks/useDeliveryDate.ts
import { useState, useEffect, useRef } from 'react';
import { calculateDeliveryDate, getDefaultDeliveryText, getUserLocation } from '../utils/deliveryDates';

const deliveryCache = new Map<string, { date: string; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 минут

export const useDeliveryDate = (userId?: string) => {
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const isMountedRef = useRef(true);
    const timeoutRef = useRef<number | undefined>(undefined); // ✅ Исправлено: добавляем undefined

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const loadDeliveryDate = async () => {
            const cacheKey = userId || 'guest';
            const cached = deliveryCache.get(cacheKey);

            if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
                if (isMountedRef.current) {
                    setDeliveryDate(cached.date);
                    setLoading(false);
                }
                return;
            }

            try {
                let location = null;
                if (userId) {
                    location = await getUserLocation(userId);
                }

                const deliveryInfo = await calculateDeliveryDate(false, location || undefined);
                const dateText = deliveryInfo.shortText;

                deliveryCache.set(cacheKey, {
                    date: dateText,
                    timestamp: Date.now()
                });

                if (isMountedRef.current) {
                    setDeliveryDate(dateText);
                }
            } catch (error) {
                console.error('Error loading delivery date:', error);
                if (isMountedRef.current) {
                    setDeliveryDate(getDefaultDeliveryText(false));
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        };

        timeoutRef.current = window.setTimeout(() => {
            loadDeliveryDate();
        }, 0);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [userId]);

    return { deliveryDate, loading };
};